import Vue from 'vue'
import { day6Analytics } from '@/util/analyticsUtil'
import setupMockUnity from './mockUnity'

const mainReceiver = 'MainNavigation'
const buildReceiver = 'BodyBuilder'
const poseReceiver = 'PoseSceneManager'
const previewReceiver = 'PreviewControl'

const SceneKeys = {
  Main: 'main',
  Build: 'body-build',
  Preview: 'preview-character',
  Pose: 'pose-character'
}

const mixin = {
  data: function () {
    return {
      // Unity has not fully loaded (unityInstance is set the first time)
      isUnityLoading: true,
      isSceneReady: false,
      isMainScene: false,
      isEditScene: false,
      isPreviewScene: false,
      isPoseScene: false,
      dimensionsChangeCounter: 0,
      poseChangeCounter: 0,
      previewSettingsChangeCounter: 0,
      // TRUE when sync was requested but Unity was not yet loaded/ready
      isPendingUnitySync: false,
      // Validating requests between Unity and js
      unityMessageRequestCounter: 1,
      unityRequestFulfillment: {},
      isPlayingAnimation: false,
      isApplyEstimateModal: false,
      isGeneratingAnimation: false,
      isGenerateLocomotionModal: false,
      frameCountCache: 0
    }
  },
  methods: {
    resetPoseSceneState () {
      this.isPlayingAnimation = false
      this.isApplyEstimateModal = false
      this.isGeneratingAnimation = false
      this.isGenerateLocomotionModal = false
    },
    deleteUnityWebGlMethods () {
      delete window.onBodyDimensionChangeJs
      delete window.onFullBodyDimensionsJs
      delete window.onPoseRetargetJs
      delete window.onBodyDimensionsColorJs
      delete window.onCharacterPoseChangeJs
      delete window.onCharacterPoseJs
      delete window.onPendingApplyEstimateJs
      delete window.onPendingGenerateLocomtionJs
      delete window.onGenerateLocomotionStartJs
      delete window.onAnimationChangeJs
      delete window.onCharacterAnimationJs
      delete window.onToggleFrameInfoJs
      delete window.onOrderedFramesJs
      delete window.onModifyKeyFramesResultJs
      delete window.onPreviewSceneChangeJs
      delete window.onPreviewSceneStateJs
      delete window.onPreviewAnimationResultJs
      delete window.onPreviewAnimationChangeJs
      delete window.onErrorMessageJs
      delete window.onSceneOpened
      delete window.onSceneReady
      delete window.onOpeningScene
      delete window.onCaptureAnalytics
      delete window.onPoseV1EncounteredJs
      delete window.onToggleColorPickerJs
      delete window.onBlockedOperationJs
      delete window.onOpenModalJs
      delete window.onModeChangeJs

      document.removeEventListener('keydown', this.unityClipboardListener)
    },
    setUnityWebGlMethods () {
      window.onBodyDimensionChangeJs = () => {
        Vue.logger.log('Body dimension changed')

        this.dimensionsChangeCounter++

        this.clearBuildData()

        this.debouceAutosaveDimensions()
      }
      window.onFullBodyDimensionsJs = payload => {
        if (!this.defaultBodyDimensions) { return }

        const { requestKey, bodyDimensions } = JSON.parse(payload)
        this.bodyDimensions = Object.assign({ ...this.defaultBodyDimensions }, bodyDimensions)
        this.unityRequestFulfillment[requestKey] = bodyDimensions
      }
      window.onPoseRetargetJs = (poseJson, poseDimensionsJson) => {
        try {
          const pose = JSON.parse(poseJson)
          const poseBodyDimensions = JSON.parse(poseDimensionsJson)
          const bodyDimensions = poseBodyDimensions.bodyDimensions ? poseBodyDimensions.bodyDimensions : poseBodyDimensions
          this.characterPoseCache = pose
          this.poseBodyDimensionsCache = bodyDimensions
        } catch (e) {
          Vue.logger.error('Unexpected Unity pose retarget payloads')
        }
      }
      window.onBodyDimensionsColorJs = (color) => this.updateColorPickerColor(color)
      window.onCharacterPoseChangeJs = () => {
        Vue.logger.log('Character pose changed')

        this.poseChangeCounter++

        this.debouceAutosavePose()
      }
      window.onCharacterPoseJs = payload => {
        const { requestKey, pose } = JSON.parse(payload)
        this.unityRequestFulfillment[requestKey] = pose
      }
      const clearUnityRequest = payload => {
        if (payload) {
          try {
            // Modal needs to be addressed.
            const result = JSON.parse(payload)
            if (result && result.requestKey) {
              // The request made is empty so clean up entry.
              this.unityRequestFulfillment[result.requestKey] = undefined
            }
          } catch (e) {
            Vue.logger.error(e)
          }
        }
      }
      window.onPendingApplyEstimateJs = payload => {
        clearUnityRequest(payload)
        this.isSceneReady = true
        this.poseEstimateApplyPendingDialog()
      }
      window.onPendingGenerateLocomtionJs = payload => {
        clearUnityRequest(payload)
        this.isSceneReady = true
        this.generateLocomotionPendingDialog()
      }
      window.onGenerateLocomotionStartJs = () => {
        // Generate locomotion cancels other modals
        this.isEstimatingPose = false
      }
      window.onAnimationChangeJs = (frame, count, isPlaying) => {
        this.isPlayingAnimation = isPlaying
        this.frameCountCache = count
        if (!isPlaying) {
          this.updateKeyframeTimelineFrame(frame, count, true)
          this.unityRequestKeyframes()
        }
      }
      window.onCharacterAnimationJs = payload => {
        const { requestKey, animationData } = JSON.parse(payload)
        this.unityRequestFulfillment[requestKey] = animationData
      }
      window.onToggleFrameInfoJs = () => {
        this.openKeyframeTimeline = !this.openKeyframeTimeline
      }
      window.onOrderedFramesJs = payload => {
        try {
          const { requestKey, frames } = JSON.parse(payload)
          if (requestKey !== this._urkKeyframes) { return }

          this.animationKeyframes = frames
        } catch (e) {
          Vue.logger.error(e)
        }
      }
      window.onModifyKeyFramesResultJs = payload => {
        try {
          const { requestKey, modifyKey, result } = JSON.parse(payload)

          if (requestKey !== this._urkModifyKeyframes) { return }

          this.onKeyframeTimelineOperation(!result)

          if (result) {
            Vue.logger.error(Error(`${modifyKey} ${result}`))
          }
        } catch (e) {
          Vue.logger.error(e)
        }
      }
      window.onPreviewSceneChangeJs = () => {
        this.previewSettingsChangeCounter++
      }
      window.onPreviewSceneStateJs = (payload) => {
        if (!(this.isPreviewSettingsChanged && payload)) { return }

        try {
          const { requestKey, sceneState } = JSON.parse(payload)
          if (requestKey) {
            this.unityRequestFulfillment[requestKey] = sceneState
          } else {
            this.previewSettingsCache = sceneState
            this.saveDirtyPreview()
          }
        } catch (e) {
          Vue.logger.error(e)
        }
      }
      window.onPreviewAnimationResultJs = (resultKey, isAnimation, errorMessage) => {
        const isPreviewFail = resultKey || errorMessage
        if (isPreviewFail) {
          this.previewAnimationLoadFailDialog()
        }
        this.unityShowToggles(!isPreviewFail)

        this.showPreviewPlayback = this.isPreviewScene && !isPreviewFail && isAnimation
        this.isPreviewPlayingAnimation = false
      }
      window.onPreviewAnimationChangeJs = isPlaying => {
        this.isPreviewPlayingAnimation = isPlaying
      }
      window.onErrorMessageJs = (errorMessage) => {
        // TODO If is main scene and main reports error load body build with current. Show dialog error if possible.
        const [sceneKey, codeMessage] = errorMessage.split(':', 2)
        const [code, message] = codeMessage.split(' ', 2)
        switch (sceneKey) {
          case SceneKeys.Pose:
            this.onUnityPoseError(code, message)
            break
        }

        Vue.logger.error(new Error(errorMessage))
      }
      window.onSceneOpened = (sceneKey) => {
        this.isSceneReady = false

        this.isMainScene = sceneKey === SceneKeys.Main
        this.isEditScene = sceneKey === SceneKeys.Build
        this.isPreviewScene = sceneKey === SceneKeys.Preview
        this.isPoseScene = sceneKey === SceneKeys.Pose
        this.focusUnity()

        if (this.isEditScene) {
          this.dimensionsChangeCounter = 0
          this.dimensionsSaveCounter = 0
        } else if (this.isPoseScene) {
          this.resetPoseSceneState()
          this.resetPoseCounters()
          this.unityEnableAnimations()
        } else if (this.isPreviewScene) {
          // TODO Should this be here or elsewhere? When/where should it reset?
          this.previewSettingsChangeCounter = 0
          this.previewSettingsSaveCounter = 0
        }

        if (!this.isEditScene) {
          this.hideColorPicker()
        }
      }
      window.onSceneReady = () => {
        const isFirstLoad = this.isUnityLoading
        this.isUnityLoading = false
        this.isSceneReady = true
        if (isFirstLoad) {
          // TODO Add delay if necessary as previous logic needed to
          this.unitySyncLocalState()
        } else if (this.isPendingUnitySync) {
          this.isPendingUnitySync = false
          this.unitySyncLocalState()
        } else if (this.isPendingPoseEstimate) {
          this.isPendingPoseEstimate = false
          if (this.isPoseScene) {
            this.onEstimatePose()
          }
        } else {
          if (this.isPoseScene) {
            this.loadActivePoseSequence()
          } else if (this.isPreviewScene) {
            if (this.previewSettingsCache) {
              this.unityLoadPreviewSettings(this.previewSettingsCache)
            }
            this.unityShowToggles()
            this.unityPreviewLoadAnimation()
          }

          Vue.logger.log('Not first load nor pending sync', [
            this.isMainScene,
            this.isEditScene,
            this.isPreviewScene,
            this.isPoseScene
          ])
        }
      }
      window.onOpeningScene = (sceneKey) => {
        this.isMainScene = false
        this.isEditScene = false
        this.isPreviewScene = sceneKey === 'preview-character'
        this.isPoseScene = false
      }
      window.onCaptureAnalytics = (analyticsType, key) => {
        switch (analyticsType) {
          case 'action':
            day6Analytics.actionPress(key)
            break
          default:
            Vue.logger.log(`Unrecognized analytics type ${analyticsType} with ${key}`)
            break
        }
      }
      window.onPoseV1EncounteredJs = () => {
        // Log error so is not ignored on prod
        Vue.logger.error(new Error(`v1 pose loaded ${this.activeDimensionId}`))
      }
      window.onToggleColorPickerJs = (hexColor) => {
        this.toggleColorPicker(!!hexColor)
        if (hexColor) {
          this.updateColorPickerColor(hexColor)
        }
      }
      window.onBlockedOperationJs = (key) => {
        switch (key) {
          case 'blocked-pose-detection-during-generation':
            this.animationGenerationInProgressDialog()
            break
          default:
            Vue.logger.error(new Error(`Unrecognized blocked operation ${key}`))
        }
      }
      // Modal is open if key is defined
      window.onOpenModalJs = (modalKey) => {
        if (this._openModalCallback) {
          this._openModalCallback(modalKey)
          this._openModalCallback = null
        }
      }
      window.onModeChangeJs = (isApplyEstimateModal, isGenerateLocomotionActive, isGenerateLocomotionModal) => {
        this.isApplyEstimateModal = isApplyEstimateModal
        this.isGeneratingAnimation = isGenerateLocomotionActive
        this.isGenerateLocomotionModal = isGenerateLocomotionModal
      }
    },
    onUnityLoading ({ progress }) {
      this.unityCanvasLoadOpacity = progress
    },
    onUnityLoaded ({ isMock, unityInstance }) {
      this.setConsistentCanvasSize()

      this.unityInstance = unityInstance
      this.unitySyncLocalState()

      if (process.env.NODE_ENV !== 'production' &&
          process.env.VUE_APP_ENVIRONMENT !== 'production' &&
          isMock) {
        setupMockUnity({
          unityInstance,
          buildReceiver,
          bodyDimensions: () => this.bodyDimensions
        })
        this.unityCanvasLoadOpacity = 1
        window.onSceneReady()
      }

      document.removeEventListener('keydown', this.unityClipboardListener)
      document.addEventListener('keydown', this.unityClipboardListener)
    },
    onUnityError ({ message }) {
      this.unityLoadErrorDialog()
      if (!message) {
        message = 'Script load fail'
      }
      Vue.logger.error(new Error(message))
    },
    unityClipboardListener (e) {
      if (!this.isPoseScene) { return }

      if (e.altKey) { return }

      if (e.code === 'KeyZ') {
        const isMacOs = /\bmac os\b/i.test(navigator.userAgent)
        const cycleClipboard = (isMacOs && e.metaKey) || (!isMacOs && e.ctrlKey)
        if (cycleClipboard) {
          e.preventDefault()

          const cycleDirection = e.shiftKey ? 1 : 0
          this.unityInstance.SendMessage(poseReceiver, 'CycleClipboardUnity', cycleDirection)
        }
      }
    },
    focusUnity () {
      this.$nextTick(() => { this.$refs.unityContainer.focusOnUnity() })
    },
    /**
     * Syncs editor with local state
     *
     * For initial load, resetting dimensions, or loading any dimensions from the network.
     */
    async unitySyncLocalState () {
      if (this.isOpeningLoad || !this.isSceneReady) {
        this.isPendingUnitySync = true
        return
      }

      if (this.isEditScene || this.isPoseScene || this.isMainScene) {
        const getJsonDimensions = () => JSON.stringify(this.bodyDimensions)

        const assetUrl = this.fileDownloadUrl

        let pose = ''
        if (assetUrl && this.characterPoseCache && (this.isPoseScene || this.isMainScene)) {
          pose = JSON.stringify(this.characterPoseCache)
        }

        if (this.isEditScene) {
          this.unityInstance.SendMessage(buildReceiver, 'SyncBodyDimensionsUnity', getJsonDimensions())
        } else if (this.isPoseScene) {
          this.unityInstance.SendMessage(poseReceiver, 'SyncCharacterPoseUnity', pose)
        } else {
          this.unityInstance.SendMessage(mainReceiver, 'SetThinScreenUnity', this.isSmallScreen ? 1 : 0)
          this.unityInstance.SendMessage(mainReceiver, 'SetExternalColorControlUnity', 1)
          this.unityInstance.SendMessage(mainReceiver, 'SetBodyDimensionsUnity', getJsonDimensions())
          // this.unityInstance.SendMessage(mainReceiver, 'SetZoomScaleUnity', 1)
          if (this.isShareOriginating && assetUrl && pose) {
            this.unityInstance.SendMessage(mainReceiver, 'SetCharacterPoseUnity', pose)
            this.unityInstance.SendMessage(mainReceiver, 'OpenPreviewCharacterUnity', assetUrl)
          } else {
            this.unityInstance.SendMessage(mainReceiver, 'OpenBodyBuildUnity')
          }
        }
        this.isPendingUnitySync = false
      } else {
        this.isPendingUnitySync = true
      }
    },
    unityRequestBuild () {
      if (!this.isEditScene) { return }

      const requestKey = `dimensions-${this.unityMessageRequestCounter++}`
      this.unityInstance.SendMessage(buildReceiver, 'SendFullBodyDimensionsUnity', requestKey)
      return requestKey
    },
    unityBuildBodySetPose (poseJson, dimensionsJson, unityMethod, assetUrl) {
      this.unityInstance.SendMessage(buildReceiver, 'SetCharacterPoseUnity', poseJson)
      // Call only matters if pose exists
      if (poseJson && dimensionsJson) {
        this.unityInstance.SendMessage(buildReceiver, 'SetPoseBodyDimensionsUnity', dimensionsJson)
      }
      this.isSceneReady = false
      this.unityInstance.SendMessage(buildReceiver, unityMethod, assetUrl)
    },
    unityPreviewAsset (assetUrl, { poseJson, dimensionsJson }) {
      if (!assetUrl) { return }

      if (this.isEditScene) {
        this.hideColorPicker()
        this.unityBuildBodySetPose(poseJson, dimensionsJson, 'PreviewNetworkModelUnity', assetUrl)
        this.closeBodyBuildDialogs()
        this.queryPreviewPoseSequenceCache()
      }
    },
    unityRequestPose () {
      if (!this.isPoseScene) { return }

      // Don't save pose if pose has yet to change
      if (!(this.characterPoseCache || this.isPoseChanged)) { return }

      const requestKey = `pose-${this.unityMessageRequestCounter++}`
      this.unityInstance.SendMessage(poseReceiver, 'SendCharacterPoseUnity', requestKey)
      return requestKey
    },
    unityToggleBuildPoseScene ({ poseJson, dimensionsJson }) {
      if (this.isEditScene) {
        const assetUrl = this.fileDownloadUrl
        if (assetUrl) {
          this.hideColorPicker()
          this.unityBuildBodySetPose(poseJson, dimensionsJson, 'PoseCharacterUnity', assetUrl)
          this.closeBodyBuildDialogs()
        }
      } else if (this.isPoseScene) {
        this.isSceneReady = false
        this.unityInstance.SendMessage(poseReceiver, 'OpenBodyBuildUnity')
        this.closePoseDialogs()
      }
    },
    unityApplyPoseEstimate (poseEstimate) {
      if (!this.isPoseScene) { return }

      const poseEstimateJson = JSON.stringify(poseEstimate)
      this.unityInstance.SendMessage(poseReceiver, 'ApplyPoseEstimateUnity', poseEstimateJson)
    },
    unityCancelPoseEstimate () {
      if (this.isPoseScene) {
        this.unityInstance.SendMessage(poseReceiver, 'CancelPoseEstimateUnity')
      }
    },
    // Due to lacking Unity callback mechanism...
    async waitForUnityResponse (requestKey) {
      const timeoutTriesMax = 3
      let timeoutTries = 0
      let timeoutDelayMs = 20
      const fulfillment = this.unityRequestFulfillment
      return new Promise((resolve, reject) => {
        const checkOnRequest = () => {
          if (requestKey in fulfillment) {
            const response = fulfillment[requestKey]
            delete fulfillment[requestKey]
            resolve(response)
            return
          }

          timeoutTries++
          if (timeoutTries >= timeoutTriesMax) {
            reject(new Error(`Unity request ${requestKey} never fulfilled`))
          } else {
            timeoutDelayMs = timeoutDelayMs * 2
            setTimeout(checkOnRequest, timeoutDelayMs)
          }
        }

        setTimeout(checkOnRequest, timeoutDelayMs)
      })
    },

    hideColorPicker () {
      this.toggleColorPicker(false)
    },
    toggleColorPicker (show) {
      this.isColorPickerVisible = show
    },
    updateColorPickerColor (color) {
      // Ignore alpha for now
      if (color.length === 8) {
        color = color.substr(0, 6)
      }
      if (!color.startsWith('#')) {
        color = `#${color}`
      }

      // Save for compare later in case of slight changes on color conversions
      this.lastReportedUnityColor = color
      this.selectedColor = color
    },
    unityChangeColor (color) {
      if (color === this.lastReportedUnityColor) { return }

      if (this.isEditScene || this.isPreviewScene) {
        const receiver = this.isEditScene ? buildReceiver : previewReceiver
        this.unityInstance.SendMessage(receiver, 'ExternalChangeColorUnity', color)
      }
    },

    unityLoadPreviewSettings (settings) {
      if (!(this.isPreviewScene && settings)) { return }

      const settingsJson = JSON.stringify(settings)
      this.unityInstance.SendMessage(previewReceiver, 'SyncPreviewSettingsUnity', settingsJson)
    },
    unityRequestPreview () {
      if (!(this.isPreviewScene && this.isPreviewSettingsChanged)) { return }

      const requestKey = `preview-${this.unityMessageRequestCounter++}`
      this.unityInstance.SendMessage(previewReceiver, 'SendPreviewSceneStateUnity', requestKey)
      return requestKey
    },
    unityPreviewOpenBodyBuild () {
      if (!this.isPreviewScene) { return }

      this.unityInstance.SendMessage(previewReceiver, 'OpenBodyBuildUnity')
    },
    async unityEnableAnimations () {
      if (!this.isPoseScene) { return }

      const enable = this.isAccountPaid ? 1 : 0
      if (enable) {
        await this.loadCharacterAnimations()
      }
      this.unityInstance.SendMessage(poseReceiver, 'EnableAnimationsUnity', enable)
    },
    unityLogLocomotionParameters () {
      if (!this.isPoseScene) { return }

      this.unityInstance.SendMessage(poseReceiver, 'LogGenerationParametersUnity')
    },
    unityCancelLocomotionGeneration () {
      if (!this.isPoseScene) { return }

      this.unityInstance.SendMessage(poseReceiver, 'CancelGenerateLocomotionUnity')
    },
    unityForceClearAnimation () {
      if (!this.isPoseScene) { return }

      this.unityInstance.SendMessage(poseReceiver, 'ClearAnimationUnity', 1)
    },
    unityQueryOpenModal () {
      if (this.isPoseScene) {
        this.unityInstance.SendMessage(poseReceiver, 'QueryOpenModalUnity')
      } else {
        Vue.logger.error(new Error(`Modal was queried in unexpected scene ${this.isEditScene} ${this.isPreviewScene}`))
      }
    },
    // TODO Test and delete comment
    unityCloseModals () {
      if (this.isPoseScene) {
        this.unityInstance.SendMessage(poseReceiver, 'CloseModalUnity')
      }
    },
    unityLoadPoseSequence (poseSequenceData) {
      if (!this.isPoseScene) { return }

      // TODO How to determine if loaded or not. High risk of loss of important data if not loaded properly and changes are saved (improperly)...
      const sequenceJson = JSON.stringify(poseSequenceData)
      this.unityInstance.SendMessage(poseReceiver, 'SyncCharacterAnimationUnity', sequenceJson)
    },
    unityRequestKeyframes () {
      if (!this.isPoseScene) { return }

      const requestKey = `keyframes-${this.unityMessageRequestCounter++}`
      this._urkKeyframes = requestKey
      this.unityInstance.SendMessage(poseReceiver, 'GetPoseFramesUnity', requestKey)
    },
    unityFocusFrameIndex (frameIndex) {
      if (!this.isPoseScene) { return }

      this.unityInstance.SendMessage(poseReceiver, 'SeekAnimationFrameUnity', frameIndex)
    },
    _unityModifyKeyframes (data, methodName) {
      const requestKey = `modify-keyframes-${this.unityMessageRequestCounter++}`
      this._urkModifyKeyframes = requestKey
      const messageData = JSON.stringify(Object.assign({ requestKey }, data))
      this.unityInstance.SendMessage(poseReceiver, methodName, messageData)
    },
    unityShiftKeyframes (frameDelta, keyframeIndexes) {
      if (!this.isPoseScene) { return }

      this._unityModifyKeyframes({
        frameShift: frameDelta,
        frameIndexes: keyframeIndexes
      }, 'ShiftKeyFramesUnity')
    },
    unityDeleteKeyframes (keyframeIndexes) {
      if (!this.isPoseScene) { return }

      this._unityModifyKeyframes({
        frameIndexes: keyframeIndexes
      }, 'DeleteKeyFramesUnity')
    },
    unityTogglePlayPause () {
      if (!this.isPoseScene) { return }

      this.unityInstance.SendMessage(poseReceiver, 'TogglePlayPauseAnimationUnity')
      this.focusUnity()
    },
    unityPoseSequenceNavigate ({ direction, startEnd, orient, keyframe }) {
      if (!this.isPoseScene) { return }

      let seekMethod = ''
      if (startEnd) {
        seekMethod = direction > 0 ? 'SeekAnimationSequenceEndUnity' : 'SeekAnimationSequenceStartUnity'
        direction = 0
      } else if (keyframe) {
        seekMethod = 'SeekKeyFrameUnity'
      } else if (orient) {
        seekMethod = 'SeekOrientKeyFrameUnity'
      } else {
        seekMethod = direction > 0 ? 'SeekAnimationSequenceNextUnity' : 'SeekAnimationSequencePreviousUnity'
        direction = 0
      }

      if (seekMethod) {
        if (direction) {
          this.unityInstance.SendMessage(poseReceiver, seekMethod, direction)
        } else {
          this.unityInstance.SendMessage(poseReceiver, seekMethod)
        }
      }
    },
    unityShowToggles (isPoseSequenceLoaded) {
      if (!this.isPreviewScene) { return }

      const isPosed = isPoseSequenceLoaded || this.characterPoseCache
      const showSettingsToggle = this.activeDimensionId && isPosed
      if (!showSettingsToggle) { return }

      this.unityInstance.SendMessage(previewReceiver, 'ShowColorPickerToggleUnity')
      this.unityInstance.SendMessage(previewReceiver, 'ShowCameraSettingsToggleUnity')
    },
    unityPreviewLoadAnimation () {
      if (this.isPreviewScene && this.isSceneReady && this.previewPoseSequenceCache) {
        const poseSequenceJson = JSON.stringify(this.previewPoseSequenceCache)
        this.unityInstance.SendMessage(previewReceiver, 'PreviewAnimationUnity', poseSequenceJson)
      }
    },
    unityPreviewTogglePlay () {
      if (!this.isPreviewScene) { return }

      this.unityInstance.SendMessage(previewReceiver, 'TogglePlayAnimationUnity')
    },
    unityPreviewRewindAnimation () {
      if (!this.isPreviewScene) { return }

      this.unityInstance.SendMessage(previewReceiver, 'RewindAnimationUnity')
    }
  }
}

export default mixin
